<template>
  <div class="recon-overview">
    <EntraPageHeader
      title="Recon Overview"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadOverview"
    />
    <div class="page-body">
      <div v-if="error" class="error-message">{{ error }}</div>

      <div class="card">
        <div class="overview-filters">
          <div class="filter-group">
            <label class="filter-label">Overview Type:</label>
            <select v-model="overviewType" class="filter-select" @change="loadOverview">
              <option value="clients">Client Overview</option>
              <option value="products">Product Overview</option>
              <option value="ap">AP Overview</option>
              <option value="accrual">Accrual Variance</option>
            </select>
          </div>
          <div class="filter-group" v-if="overviewType !== 'ap' && overviewType !== 'accrual'">
            <label class="filter-label">Recon Type:</label>
            <select v-model="reconType" class="filter-select" @change="loadOverview">
              <option value="M365">M365</option>
              <option value="Azure">Azure</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Billing Month:</label>
            <select v-model="billingMonth" class="filter-select" @change="loadOverview">
              <option v-for="m in availableMonths" :key="m" :value="m">{{ m }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="card" v-if="overviewType === 'clients'">
        <h3>Client Overview — {{ reconType }} {{ billingMonth }}</h3>
        <div v-if="state === 'loading'" class="loading-state"><span>Loading...</span></div>
        <div v-else-if="items && items.length > 0">
          <table class="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Attention</th>
                <th>Bill Split</th>
                <th>Entity</th>
                <th>Class ID</th>
                <th style="text-align: right;">Cost</th>
                <th style="text-align: right;">Charge</th>
                <th style="text-align: right;">Cost USD</th>
                <th style="text-align: right;">Charge USD</th>
                <th style="text-align: right;">Margin</th>
                <th style="text-align: right;">Waived</th>
                <th style="text-align: center;">Bill</th>
                <th style="text-align: right;">Items</th>
                <th style="text-align: right;">Products</th>
                <th>Prepay Split</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in items" :key="idx">
                <td>{{ item.customer_name_clean }}</td>
                <td>
                  <span v-if="attentionMap[item.customer_name_clean] === 'new'" class="badge badge-new" title="No Client Billing Dates entry — configure on Recon Config">New</span>
                  <span v-else-if="attentionMissing(item.customer_name_clean)" class="badge badge-config" :title="'Missing: ' + attentionMissing(item.customer_name_clean)">Config</span>
                  <span v-else class="badge badge-ok">—</span>
                </td>
                <td>{{ item.bill_split }}</td>
                <td>{{ item.intacct_entity || '--' }}</td>
                <td>{{ item.class_id || '--' }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_cost) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_charge) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_cost_usd, 'USD') }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_charge_usd, 'USD') }}</td>
                <td style="text-align: right;">{{ item.client_charge_usd ? ((item.client_charge_usd - item.client_cost_usd) / item.client_charge_usd * 100).toFixed(2) + '%' : '--' }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_waived) }}</td>
                <td style="text-align: center;">{{ item.generate_bill ? 'Yes' : 'No' }}</td>
                <td style="text-align: right;">{{ item.line_item_count }}</td>
                <td style="text-align: right;">{{ item.product_count }}</td>
                <td>{{ item.prepay_time_split || '--' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="no-data">No data available for the selected month.</p>
      </div>

      <div class="card" v-if="overviewType === 'products'">
        <h3>Product Overview — {{ reconType }} {{ billingMonth }}</h3>
        <div v-if="state === 'loading'" class="loading-state"><span>Loading...</span></div>
        <div v-else-if="items && items.length > 0">
          <table class="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Clean SKU</th>
                <th style="text-align: right;">Discount</th>
                <th style="text-align: right;">Quantity</th>
                <th style="text-align: right;">Total Spend</th>
                <th>Currency</th>
                <th style="text-align: right;">Customers</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in items" :key="idx">
                <td>{{ item.product_name }}</td>
                <td>{{ item.sku_name }}</td>
                <td>{{ item.clean_sku || '--' }}</td>
                <td style="text-align: right;">{{ (Number(item.discount_pct) * 100).toFixed(2) }}%</td>
                <td style="text-align: right;">{{ item.total_quantity }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.total_spend, item.currency) }}</td>
                <td>{{ item.currency }}</td>
                <td style="text-align: right;">{{ item.customer_count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="no-data">No data available for the selected month.</p>
      </div>

      <div class="card" v-if="overviewType === 'ap'">
        <h3>AP Overview — {{ billingMonth }}</h3>
        <div v-if="state === 'loading'" class="loading-state"><span>Loading...</span></div>
        <div v-else-if="items && items.length > 0">
          <table class="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th style="text-align: center;">Bill</th>
                <th>Parent ID</th>
                <th>Entity</th>
                <th>Category</th>
                <th>Class ID</th>
                <th style="text-align: right;">Cost</th>
                <th style="text-align: right;">Tax</th>
                <th style="text-align: right;">Charge</th>
                <th style="text-align: right;">Cost USD</th>
                <th style="text-align: right;">Tax USD</th>
                <th style="text-align: right;">Charge USD</th>
                <th>MSFT ID</th>
                <th>Invoice Period</th>
                <th>CSP</th>
                <th>Opp ID</th>
                <th style="text-align: right;">Invoices</th>
                <th>Posting Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in items" :key="idx">
                <td>{{ item.customer_name_clean }}</td>
                <td style="text-align: center;">{{ item.generate_bill ? 'Yes' : 'No' }}</td>
                <td>{{ item.parent_id || '--' }}</td>
                <td>{{ item.intacct_entity || '--' }}</td>
                <td>{{ item.ap_category || '--' }}</td>
                <td>{{ item.class_id || '--' }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_cost) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_tax) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_charge) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_cost_usd, 'USD') }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_tax_usd, 'USD') }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.client_charge_usd, 'USD') }}</td>
                <td>{{ item.msft_id || '--' }}</td>
                <td>{{ item.invoice_period || '--' }}</td>
                <td>{{ item.invoice_csp || '--' }}</td>
                <td>{{ item.opp_id || '--' }}</td>
                <td style="text-align: right;">{{ item.invoice_count }}</td>
                <td>{{ item.posting_date ? formatDate(item.posting_date, 'short') : '--' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="no-data">No data available for the selected month.</p>
      </div>

      <div class="card" v-if="overviewType === 'accrual'">
        <div class="card-header-row">
          <h3>Accrual Variance — {{ accrualMeta.month || billingMonth }} vs {{ accrualMeta.priorMonth }}</h3>
          <button class="dl-btn" @click="downloadAccrualCsv">Download CSV</button>
        </div>
        <p class="accrual-note">
          Flags clients whose month-over-month charge moves more than {{ accrualMeta.thresholdPct }}%.
          {{ accrualMeta.flaggedCount }} flagged of {{ items.length }} billed clients.
        </p>
        <div v-if="state === 'loading'" class="loading-state"><span>Loading...</span></div>
        <div v-else-if="items && items.length > 0">
          <table class="data-table">
            <thead>
              <tr>
                <th>Flag</th>
                <th>Client</th>
                <th>Entity</th>
                <th style="text-align: right;">{{ accrualMeta.priorMonth }} Charge</th>
                <th style="text-align: right;">{{ accrualMeta.month }} Charge</th>
                <th style="text-align: right;">Variance</th>
                <th style="text-align: right;">Variance %</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in items" :key="idx" :class="{ 'flagged-row': item.flagged }">
                <td><span v-if="item.flagged" class="badge badge-config">⚑</span><span v-else class="badge badge-ok">—</span></td>
                <td>{{ item.customerName }}</td>
                <td>{{ item.intacctEntity || '--' }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.priorMonthCharge) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.currentMonthCharge) }}</td>
                <td style="text-align: right;">{{ formatCurrency(item.variance) }}</td>
                <td style="text-align: right;">{{ item.variancePct === null ? 'new' : item.variancePct.toFixed(2) + '%' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="no-data">No accrual data for the selected month.</p>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { formatCurrency } from '@/services/entra-utils.js';
import { formatDate } from '@/services/date-utils.js';
import {
  getReconStatus,
  getReconClientOverview,
  getReconProductOverview,
  getReconApOverview,
  getReconAttention,
  getReconAccrualVariance
} from '@/services/billing-api.js';

export default {
  name: 'ReconOverview',
  components: { EntraPageHeader },
  data: function() {
    return {
      error: '',
      overviewType: 'clients',
      reconType: 'M365',
      billingMonth: '',
      availableMonths: [],
      state: 'loaded',
      items: [],
      attentionMap: {},
      accrualMeta: { month: '', priorMonth: '', thresholdPct: 10, flaggedCount: 0 }
    };
  },
  methods: {
    formatCurrency: function(amount, currency) { return formatCurrency(amount, currency); },
    formatDate: formatDate,
    attentionMissing: function(clientName) {
      var entry = this.attentionMap[clientName];
      if (!entry || entry === 'new') return null;
      return entry.join(', ');
    },
    downloadAccrualCsv: function() {
      var self = this;
      getReconAccrualVariance({ month: this.billingMonth, format: 'csv' }).then(function(data) {
        var blob = new Blob([data], { type: 'text/csv' });
        var url = window.URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.download = 'AR_Accrual_Variance_' + self.billingMonth + '.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }).catch(function(err) { self.error = err.message; });
    },
    loadAttention: function() {
      var self = this;
      this.attentionMap = {};
      if (this.overviewType !== 'clients' || !this.billingMonth) return;
      getReconAttention({ reconType: this.reconType, billingMonth: this.billingMonth }).then(function(data) {
        var map = {};
        (data.unlisted || []).forEach(function(name) { map[name] = 'new'; });
        (data.incomplete || []).forEach(function(c) {
          if (map[c.clientName] !== 'new') map[c.clientName] = c.missing;
        });
        self.attentionMap = map;
      }).catch(function() {
        self.attentionMap = {};
      });
    },
    loadAvailableMonths: function() {
      var self = this;
      getReconStatus().then(function(data) {
        var months = [];
        var seen = {};
        (data.items || []).forEach(function(item) {
          if (!seen[item.billing_month]) {
            seen[item.billing_month] = true;
            months.push(item.billing_month);
          }
        });
        months.sort(function(a, b) { return b.localeCompare(a); });
        self.availableMonths = months;
        if (months.length > 0 && !self.billingMonth) {
          self.billingMonth = months[0];
          self.loadOverview();
        }
      }).catch(function() {});
    },
    loadOverview: function() {
      var self = this;
      this.error = '';
      this.state = 'loading';
      var apiFn;
      if (this.overviewType === 'clients') {
        apiFn = getReconClientOverview({ reconType: this.reconType, billingMonth: this.billingMonth });
      } else if (this.overviewType === 'products') {
        apiFn = getReconProductOverview({ reconType: this.reconType, billingMonth: this.billingMonth });
      } else if (this.overviewType === 'ap') {
        apiFn = getReconApOverview({ billingMonth: this.billingMonth });
      } else if (this.overviewType === 'accrual') {
        apiFn = getReconAccrualVariance({ month: this.billingMonth });
      }
      if (apiFn) {
        apiFn.then(function(data) {
          if (self.overviewType === 'accrual') {
            self.items = data.rows || [];
            self.accrualMeta = {
              month: data.month, priorMonth: data.priorMonth,
              thresholdPct: data.thresholdPct, flaggedCount: data.flaggedCount
            };
          } else {
            self.items = data.items || [];
          }
          self.state = 'loaded';
        }).catch(function(err) {
          self.error = err.message || 'Failed to load overview.';
          self.items = [];
          self.state = 'loaded';
        });
        self.loadAttention();
      }
    }
  },
  mounted: function() {
    this.loadAvailableMonths();
  }
};
</script>

<style scoped>
.recon-overview { min-height: 100%; }
.page-body { padding: 24px; }
.card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); margin-bottom: 20px;
}
.card h3 { font-size: 0.9375rem; font-weight: 700; color: #0f172a; margin: 0 0 16px 0; }
.overview-filters { display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap; }
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-label { font-size: 0.75rem; font-weight: 600; color: #64748b; }
.filter-select, .filter-input {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px;
  font-size: 0.875rem; font-family: 'Inter', sans-serif;
}
.data-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.8125rem; }
.data-table th { text-align: left; padding: 10px 12px; background: #f1f5f9; color: #475569; font-weight: 700; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.data-table tbody tr:last-child td { border-bottom: none; }
.no-data { font-size: 0.875rem; color: #94a3b8; text-align: center; padding: 32px 16px; background: #f8fafc; border-radius: 8px; border: 1px dashed #e2e8f0; }
.error-message { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; padding: 16px; border-radius: 8px; font-size: 0.875rem; margin-bottom: 16px; }
.loading-state { color: #64748b; font-size: 0.875rem; padding: 24px; }
.badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 0.6875rem; font-weight: 700; }
.badge-new { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
.badge-config { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; cursor: help; }
.badge-ok { color: #cbd5e1; }
.card-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.card-header-row h3 { margin: 0; }
.accrual-note { font-size: 0.75rem; color: #64748b; margin: 0 0 12px 0; }
.dl-btn { background: #f1f5f9; color: #4f46e5; border: 1px solid #e2e8f0; padding: 6px 16px; border-radius: 8px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
.flagged-row { background: #fffbeb; }
</style>
